import {faStarHalfAlt} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import React, {useState} from 'react';
import {withTranslation} from 'react-i18next';
import StarRatings from 'react-star-ratings';
import styled from 'styled-components';
import {dateFormatFR} from '../../../utils';
import DataTable from '../../../common/components/Datatable/Datatable';
import DateRangePicker from '../../../common/components/Dates/DateRangePicker';
import Screen from '../../../common/components/layout/Screen';
import SelectComments from '../../../common/components/Merchant/SelectComments';
import SelectRating from '../../../common/components/Merchant/SelectRating';
import Metadata from '../../../common/components/Metadata';
import {
  FILTER_OPERATOR,
  RECEIPTS_CUSTOM_QUERY,
  FILTER_EXPRESSION_OPERATOR,
  ORDER_DIRECTION,
} from '../../../common/constants';
import DisplayBoolean from '../../../common/components/DisplayBoolean';
import TicketRowActionMerchant from './TicketRowActionMerchant';

const MargedDiv = styled.div`
  margin: 5px;
`;

const Span = styled.span`
  font-family: 'Segoe UI Bold', sans-serif;
`;

const GRAPHQL_QUERY_RECEIPTS_COMMENTS = gql`
  query receipts($criteria:CriteriaInput, $customQuery: ReceiptsCustomQuery){
    receipts(criteria: $criteria, customQuery: $customQuery){
      rows{
        id
        isDeleted
        rating
        createdAt
        comments {
          id
          review
        }
        user{
          firstName
          lastName
          email
          id
          barcode
          civility
        }
        brand{
          id
          name
          createdAt
          updatedAt
        }
        file{
          id
          key
          token
          filename
          size
          mimeType
        }
        checkout{
          id
          name
        }
        tags {
          id
          rows {
            id
            name
          }
        }
      }
      total
      id
    }
  }
`;

const displayRating = (data) => {

  return (
    data ?
      <StarRatings
        rating={data || 0}
        starDimension="20px"
        starSpacing="3px"
        starRatedColor='#C90164'
      /> : null
  );
};

const displayReviewStatus = (data) => {
  return (
    <DisplayBoolean bool={data !== undefined}/>
  );
};

const columns = t => [
  {
    Header: '#',
    accessor: 'id',
    sortable: true,
  },
  {
    Header: t('creation'),
    Cell: ({cell}) => dateFormatFR({date: cell.value}),
    sortable: true,
    accessor: 'createdAt',
  },
  {
    Header: t('rating'),
    accessor: 'rating',
    Cell: ({cell}) => displayRating(cell.value),
    sortable: true,
  },
  {
    Header: t('comment'),
    accessor: 'comments[0]',
    Cell: ({cell}) => displayReviewStatus(cell.value),
  },
  {
    Header: t('commentReply'),
    accessor: 'comments[1]',
    Cell: ({cell}) => displayReviewStatus(cell.value),
  },
  {
    Header: t('detail'),
    Cell: ({cell}) => <TicketRowActionMerchant receipt={cell.value}/>,
    accessor: row => row,
  },
];



const createFilters = (search, startDate, endDate, rating) => {
  const andFilters = [];
  const startObject = {
    field: 'receipt.createdAt',
    operator: FILTER_OPERATOR.GreaterThan,
    value: startDate,
  };
  const endObject = {
    field: 'receipt.createdAt',
    operator: FILTER_OPERATOR.LessThan,
    value: endDate,
  };

  const ratingObject = {
    field: 'receipt.rating',
    operator: FILTER_OPERATOR.Equal,
    value: rating,
  };

  if (startDate) {
    andFilters.push(startObject);
  }

  if (endDate) {
    andFilters.push(endObject);
  }

  if (+rating && rating !== 'default') {
    andFilters.push(ratingObject);
  }

  const expressions = [{
    operator: FILTER_EXPRESSION_OPERATOR.And,
    filters: andFilters,
    expressions: {
      operator: FILTER_EXPRESSION_OPERATOR.Or,
      filters: [{
        field: 'receipt.id',
        operator: FILTER_OPERATOR.Like,
        value: `%${search}%`,
      }],
    },
  }];

  return {
    expressions,
    relations: ['user', 'brand', 'file'],
  };
};


const ListReview = ({t}) => {

  const [search, setSearch] = useState('');
  const [rating, setRating] = useState('default');
  const [comments, setComments] = useState('default');

  const [{startDate, endDate}, setDateFilter] = useState({});

  const additionalFilters = (
    <>
      <MargedDiv>
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onDatesChange={({startDate, endDate}) => setDateFilter({startDate, endDate})}/>
      </MargedDiv>
      <MargedDiv>
        <SelectRating value={rating} allowNull onChange={e => setRating(e.target.value)}/>
      </MargedDiv>
      <MargedDiv>
        <SelectComments value={comments} allowNull onChange={e => setComments(e.target.value)}/>
      </MargedDiv>
    </>
  );

  return (
    <Screen
      header={<Metadata meta={t('meta')} title={t('listReview')}/>}
      leftTitle={
        <>
          <FontAwesomeIcon icon={faStarHalfAlt} color={'rgb(201,0,95)'}/>
          <Span className="route-name">{t('listReview')}</Span>
        </>
      }>
      <DataTable
        columns={columns(t)}
        keyResult="receipts"
        query={GRAPHQL_QUERY_RECEIPTS_COMMENTS}
        onSearch={value => setSearch(value)}
        advancedFilters={createFilters(search, startDate, endDate, rating)}
        customQuery={comments === 'default' ? RECEIPTS_CUSTOM_QUERY.ReceiptsWithReview : comments}
        additionalFilters={additionalFilters}
        defaultSort={{
          field: 'receipt.createdAt',
          direction: ORDER_DIRECTION.DESC,
        }}
      />
    </Screen>
  );
};

ListReview.propTypes = {
  t: PropTypes.func.isRequired,
};

export default withTranslation('merchant')(ListReview);
